import React, { useEffect, useState } from "react";
import StoreLogo from "@/components/storefront/StoreLogo";
import { socialItems } from "@/lib/social";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingCart, User, ChevronLeft, ChevronRight, ChevronUp, ArrowRight, BookOpen, Mail, MapPin, BadgeCheck, ShieldCheck, Truck } from "lucide-react";
import { Reveal } from "@/components/pharmacy/hooks";
import MobileNav from "@/components/storefront/MobileNav";
import { navLinks, footerColumns, footerLines, trustItems, isNavActive, freeOver, freeShipNote } from "@/lib/storenav";

// Look-and-feel tokens of the Books layout (colours also come from the theme: primary = green, accent = red).
export const BOOK = { serif: "'Fraunces', 'Playfair Display', Georgia, serif", dark: "#0f3d27", cream: "#f7f3ee", ink: "#1b1b18", line: "#ece7df" };

export const authorOf = (p, fallback = "") => {
  const a = (p.attributes || []).find((x) => /^(author|writer|by)$/i.test(x.name));
  return a?.value || p.brand || fallback;
};

export function BookLogo({ name, size = 30, color = "#0a7d3c", dark = false, logo = "" }) {
  if (logo) return <StoreLogo store={{ logo, name }} height={Math.max(size + 8, 40)} />;
  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden className="shrink-0"><path d="M16 8 3 5v19l13 3 13-3V5L16 8Z" fill={color} /><path d="M16 8v19" stroke="#fff" strokeWidth="1.6" /><path d="M9 11l4 .9M9 15l4 .9M23 11l-4 .9M23 15l-4 .9" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" /></svg>
      <span className="truncate max-w-[190px] font-bold tracking-tight lowercase" style={{ fontFamily: BOOK.serif, fontSize: size * 0.9, color: dark ? "#fff" : BOOK.dark }}>{name}</span>
    </span>
  );
}

/** Rotating announcement strip (coupon, shipping, tagline). */
function Announcement({ s }) {
  const msgs = [
    s.coupons?.[0] ? `Use code ${s.coupons[0].code} for ${s.coupons[0].discount_type === "percent" ? s.coupons[0].value + "%" : "₹" + s.coupons[0].value} off your order` : null,
    freeShipNote(s.store),
    s.store.tagline || `Welcome to ${s.store.name}`,
  ].filter(Boolean);
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % msgs.length), 4500); return () => clearInterval(t); }, [msgs.length]);
  return (
    <div className="text-[12px] font-medium text-white" style={{ background: BOOK.dark }} data-testid="book-announcement">
      <div className="max-w-6xl mx-auto px-5 h-8 flex items-center justify-center gap-5">
        <button onClick={() => setI((i - 1 + msgs.length) % msgs.length)} aria-label="Previous message" className="opacity-80 hover:opacity-100"><ChevronLeft size={14} /></button>
        <span key={i} className="rx-slide-up truncate">{msgs[i]}</span>
        <button onClick={() => setI((i + 1) % msgs.length)} aria-label="Next message" className="opacity-80 hover:opacity-100"><ChevronRight size={14} /></button>
      </div>
    </div>
  );
}

export function BookHeader({ s, cart }) {
  const { store, slug, c } = s;
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [stuck, setStuck] = useState(false);
  useEffect(() => { const f = () => setStuck(window.scrollY > 60); f(); window.addEventListener("scroll", f, { passive: true }); return () => window.removeEventListener("scroll", f); }, []);
  const search = (e) => { e.preventDefault(); nav(`/store/${slug}/shop${q ? `?q=${encodeURIComponent(q)}` : ""}`); };
  const links = navLinks(slug);
  const { pathname } = useLocation();
  return (
    <header className="sticky top-0 z-30 bg-white" style={{ boxShadow: stuck ? "0 8px 28px rgba(0,0,0,.08)" : "none", transition: "box-shadow .3s" }}>
      <Announcement s={s} />
      <div className="max-w-6xl mx-auto px-5 h-[72px] flex items-center gap-6">
        <Link to={`/store/${slug}`} data-testid="storefront-name" className="shrink-0"><BookLogo name={store.name} logo={store.logo} color={c.primary} /></Link>
        <form onSubmit={search} className="hidden md:flex flex-1 items-center rounded-full overflow-hidden" style={{ background: "#f2f0ec" }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search our store…" aria-label="Search books" data-testid="book-search" className="flex-1 bg-transparent outline-none text-[13px] px-5 py-3 min-w-0" />
          <button className="rx-btn inline-flex items-center gap-2 px-6 py-3 text-[13px] font-semibold text-white rounded-full" style={{ background: c.primary }}><Search size={15} /> Search</button>
        </form>
        <div className="ml-auto md:ml-0 flex items-center gap-4" style={{ color: BOOK.ink }}>
          <Link to={`/store/${slug}/track`} aria-label="Track order" className="hidden sm:block hover:opacity-70"><User size={19} /></Link>
          <span className="hidden sm:block hover:opacity-70 cursor-default" aria-hidden><Heart size={19} /></span>
          <MobileNav slug={slug} color={BOOK.ink} bg="#fff" accent={c.accent} border={BOOK.line} />
          <button onClick={() => cart.setOpen(true)} data-testid="storefront-cart-btn" aria-label="Open cart" className="relative hover:opacity-70">
            <ShoppingCart size={20} />
            <span className="absolute -top-2 -right-2.5 min-w-[17px] h-[17px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center" style={{ background: "#ea580c" }}>{cart.count}</span>
          </button>
        </div>
      </div>
      <nav className="hidden lg:block border-t" style={{ borderColor: BOOK.line }}>
        <div className="max-w-6xl mx-auto px-5 flex items-center gap-9 h-12 text-[14px] font-medium">
          {links.map(([l, to]) => <Link key={l} to={to} aria-current={isNavActive(pathname, slug, to) ? "page" : undefined} className="rx-link" style={{ color: isNavActive(pathname, slug, to) ? c.primary : BOOK.ink }} data-testid={l === "Track Order" ? "storefront-track-link" : undefined}>{l}</Link>)}
          {freeOver(store) > 0 && <span className="ml-auto text-[13px]" style={{ color: BOOK.ink }}>Free delivery over <b>₹{freeOver(store).toLocaleString("en-IN")}</b></span>}
        </div>
      </nav>
    </header>
  );
}

export function BookFooter({ s }) {
  const { store, slug, c } = s;
  const [top, setTop] = useState(false);
  useEffect(() => { const f = () => setTop(window.scrollY > 600); window.addEventListener("scroll", f, { passive: true }); return () => window.removeEventListener("scroll", f); }, []);
  const lines = footerLines(store);
  const icons = { money: BadgeCheck, verified: ShieldCheck, delivery: Truck };
  // Same structure as every theme: trust strip, store details + the three standard link columns, copyright.
  return (
    <>
      <footer className="border-t" style={{ borderColor: BOOK.line }} data-testid="storefront-footer">
        <div className="max-w-6xl mx-auto px-5 py-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {trustItems(store.requires_prescription, store.free_shipping_over).map(([key, h, d], k) => { const I = icons[key]; return (
            <Reveal key={h} delay={k * 100} className="flex items-center gap-3 justify-center text-[13px]"><I size={22} style={{ color: c.primary }} /><div><b style={{ fontFamily: BOOK.serif }}>{h}</b><div className="text-[11px]" style={{ color: "#6b665e" }}>{d}</div></div></Reveal>
          ); })}
        </div>
        <div className="max-w-6xl mx-auto px-5 py-10 grid md:grid-cols-5 gap-8 border-t" style={{ borderColor: BOOK.line }}>
          <Reveal className="md:col-span-2">
            <BookLogo name={store.name} logo={store.logo} size={34} color={c.primary} />
            <p className="text-[13px] mt-4 flex gap-2" style={{ color: "#6b665e" }}><MapPin size={14} className="mt-0.5 shrink-0" style={{ color: c.primary }} />{lines.location}</p>
            <p className="text-[13px] mt-1.5 flex gap-2" style={{ color: "#6b665e" }}><Mail size={14} className="mt-0.5 shrink-0" style={{ color: c.primary }} />{lines.support}</p>
            <div className="mt-4 flex gap-2.5">{socialItems(s).map((I) => <a key={I.network} href={I.url} target="_blank" rel="noopener noreferrer" aria-label={I.label} title={I.label} data-testid={`footer-social-${I.network}`} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "#f2f0ec", color: BOOK.ink }}><I.icon size={14} /></a>)}</div>
          </Reveal>
          {footerColumns(slug).map(([h, links], i) => (
            <Reveal key={h} delay={(i + 1) * 90}>
              <div className="text-[16px] pb-3 border-b" style={{ fontFamily: BOOK.serif, borderColor: BOOK.line }}>{h}</div>
              <ul className="space-y-2.5 mt-4 text-[13px]" style={{ color: "#4b463f" }}>{links.map(([l, to]) => <li key={l}><Link to={to} className="rx-link hover:text-black">{l}</Link></li>)}</ul>
            </Reveal>
          ))}
        </div>
        <div className="border-t text-center text-[12px] py-4" style={{ borderColor: BOOK.line, color: "#6b665e" }}>© 2026 {store.name} · Powered by <Link to="/" className="underline">Sell24x7</Link></div>
        {top && <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Back to top" className="fixed bottom-6 right-6 z-30 w-11 h-11 rounded-full flex items-center justify-center rx-slide-up text-white" style={{ background: BOOK.ink }}><ChevronUp size={20} /></button>}
      </footer>
    </>
  );
}

/** Section heading with serif title and a "Browse All" pill. */
export function BookHeading({ title, to, label = "Browse All", className = "" }) {
  return (
    <Reveal className={`flex items-center justify-between gap-4 mb-7 ${className}`}>
      <h2 className="text-[30px] md:text-[34px] leading-tight" style={{ fontFamily: BOOK.serif, color: BOOK.ink, fontWeight: 400 }}>{title}</h2>
      {to && <Link to={to} className="rx-btn shrink-0 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border text-[12px] font-semibold bg-white" style={{ borderColor: BOOK.line, color: BOOK.ink }}>{label} <ArrowRight size={13} /></Link>}
    </Reveal>
  );
}

export { BookOpen };
